import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import TrendingFlatIcon from "@material-ui/icons/TrendingFlat";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import SentimentDissatisfiedIcon from "@material-ui/icons/SentimentDissatisfied";
import SentimentNeutralIcon from "@material-ui/icons/SentimentSatisfiedAlt";
import { getSentimentTrends } from "../../services/apiGpt";
import { i18n } from "../../translate/i18n";
import { getGPTTranslation, mapI18nToGPTLanguage } from "../../utils/gptTranslations";

const useStyles = makeStyles((theme) => ({
  dialog: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 600,
    },
  },
  dialogContent: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1),
    },
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: theme.spacing(4),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sentimentCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  trendContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-around",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  trendBox: {
    textAlign: "center",
    padding: theme.spacing(2),
  },
  sentimentIcon: {
    fontSize: 48,
    marginBottom: theme.spacing(1),
  },
  positive: {
    color: theme.palette.success.main,
  },
  negative: {
    color: theme.palette.error.main,
  },
  neutral: {
    color: theme.palette.warning.main,
  },
  timelineItem: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
  timelineDot: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    marginRight: theme.spacing(2),
  },
  errorContainer: {
    padding: theme.spacing(2),
    backgroundColor: "#ffebee",
    color: "#c62828",
    borderRadius: theme.shape.borderRadius,
    border: "1px solid #ef9a9a",
  },
  keyMomentCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderLeft: `4px solid`,
  },
}));

const SentimentTrendsModal = ({ open, onClose, ticketId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Get current language for translations
  const currentLang = mapI18nToGPTLanguage(i18n.language);
  const t = (key, params) => getGPTTranslation(key, currentLang, params);

  useEffect(() => {
    if (open && ticketId) {
      loadTrends();
    } else if (!open) {
      setData(null);
      setError(null);
    }
  }, [open, ticketId]);

  const loadTrends = async () => {
    if (!ticketId) {
      setError(i18n.t("gptModals.noTicketSelected") || "No ticket selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const systemLang = i18n.language || localStorage.getItem("language") || "en";
      const result = await getSentimentTrends(ticketId, systemLang);
      setData(result);
    } catch (err) {
      console.error("[SentimentTrendsModal] Error:", err);
      setError(err.response?.data?.error || err.message || "Failed to analyze sentiment");
    } finally {
      setLoading(false);
    }
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return <SentimentSatisfiedIcon className={`${classes.sentimentIcon} ${classes.positive}`} />;
      case "negative":
        return <SentimentDissatisfiedIcon className={`${classes.sentimentIcon} ${classes.negative}`} />;
      default:
        return <SentimentNeutralIcon className={`${classes.sentimentIcon} ${classes.neutral}`} />;
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case "positive":
        return "#4caf50";
      case "negative":
        return "#f44336";
      default:
        return "#ff9800";
    }
  };

  const getTrendIcon = (start, end) => {
    const sentimentValue = { positive: 1, neutral: 0, negative: -1 };
    const startVal = sentimentValue[start] || 0;
    const endVal = sentimentValue[end] || 0;

    if (endVal > startVal) {
      return <TrendingUpIcon className={classes.positive} />;
    } else if (endVal < startVal) {
      return <TrendingDownIcon className={classes.negative} />;
    }
    return <TrendingFlatIcon className={classes.neutral} />;
  };

  const translateSentiment = (sentiment) => {
    if (!sentiment) return "N/A";
    const key = sentiment.toLowerCase();
    if (key === "positive") return t("positive");
    if (key === "negative") return t("negative");
    if (key === "neutral") return t("neutral");
    return sentiment;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      classes={{ paper: classes.dialog }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <SentimentSatisfiedIcon color="primary" />
            <Typography variant={isMobile ? "subtitle1" : "h6"}>📊 {t("sentimentTrends")}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="body2" style={{ marginTop: 16 }}>
              {t("analyzingSentimentTrends")}
            </Typography>
          </Box>
        ) : error ? (
          <Box className={classes.errorContainer}>
            <Typography variant="body2">
              <strong>Error:</strong> {error}
            </Typography>
          </Box>
        ) : data ? (
          <>
            {/* Sentiment Overview */}
            <Paper className={classes.trendContainer} elevation={0}>
              <Box className={classes.trendBox}>
                <Typography variant="caption" color="textSecondary">
                  {t("start", { sentiment: "" }).replace(": ", "")}
                </Typography>
                {getSentimentIcon(data.startSentiment)}
                <Typography variant="body2">{translateSentiment(data.startSentiment)}</Typography>
              </Box>
              <Box>
                {getTrendIcon(data.startSentiment, data.endSentiment)}
              </Box>
              <Box className={classes.trendBox}>
                <Typography variant="caption" color="textSecondary">
                  {t("end", { sentiment: "" }).replace(": ", "")}
                </Typography>
                {getSentimentIcon(data.endSentiment)}
                <Typography variant="body2">{translateSentiment(data.endSentiment)}</Typography>
              </Box>
            </Paper>

            {/* Summary */}
            {data.summary && (
              <Box className={classes.section}>
                <Typography variant="h6" gutterBottom>
                  📝 {t("conversationSummary")}
                </Typography>
                <Paper className={classes.sentimentCard} elevation={0}>
                  <Typography variant="body2">{data.summary}</Typography>
                </Paper>
              </Box>
            )}

            {/* Sentiment Timeline */}
            {data.sentimentPoints && data.sentimentPoints.length > 0 && (
              <Box className={classes.section}>
                <Typography variant="h6" gutterBottom>
                  📈 {t("sentimentProgression")}
                </Typography>
                {data.sentimentPoints.map((point, index) => (
                  <Box
                    key={index}
                    className={classes.timelineItem}
                    style={{ borderLeftColor: getSentimentColor(point.sentiment) }}
                  >
                    <Box
                      className={classes.timelineDot}
                      style={{ backgroundColor: getSentimentColor(point.sentiment) }}
                    />
                    <Box flex={1}>
                      <Typography variant="caption" color="textSecondary">
                        {formatTimestamp(point.timestamp)}
                      </Typography>
                      <Typography variant="body2">{point.message || `Point ${index + 1}`}</Typography>
                    </Box>
                    <Chip
                      label={translateSentiment(point.sentiment)}
                      size="small"
                      style={{
                        backgroundColor: getSentimentColor(point.sentiment),
                        color: "white",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Key Moments */}
            {data.keyMoments && data.keyMoments.length > 0 && (
              <Box className={classes.section}>
                <Typography variant="h6" gutterBottom>
                  ⚡ {t("keyMoments")}
                </Typography>
                {data.keyMoments.map((moment, index) => (
                  <Paper
                    key={index}
                    className={classes.keyMomentCard}
                    elevation={1}
                    style={{ borderLeftColor: getSentimentColor(moment.sentiment) }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="start">
                      <Typography variant="body2">{moment.description}</Typography>
                      <Chip
                        label={translateSentiment(moment.sentiment)}
                        size="small"
                        style={{
                          backgroundColor: getSentimentColor(moment.sentiment),
                          color: "white",
                          marginLeft: 8,
                        }}
                      />
                    </Box>
                    {moment.timestamp && (
                      <Typography variant="caption" color="textSecondary">
                        {formatTimestamp(moment.timestamp)}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box className={classes.loadingContainer}>
            <Typography variant="body2" color="textSecondary">
              {t("noSentimentDataAvailable")}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={loadTrends}
              style={{ marginTop: 16 }}
            >
              {t("analyze")}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {data && (
          <Button onClick={loadTrends} color="primary" disabled={loading}>
            {t("refresh")}
          </Button>
        )}
        <Button onClick={onClose} color="primary">
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SentimentTrendsModal;
